import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import TablePagination from '../components/TablePagination';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiPieChart, FiPlus } from 'react-icons/fi';

import logo from "../../src/assets/images/logo.png"
import serversImg from "../../src/assets/images/servers.png"
import priceBG from "../../src/assets/images/price-bg.png"
import nl from "../../src/assets/images/netherland.png"
import usa from "../../src/assets/images/usa.png"

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DollarIcon = () => (
  <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.04034 13.3889H3.27865C1.47437 13.3889 0.011055 11.8712 0.011055 10.0006C0.011055 9.54991 0.385031 9.17579 0.836243 9.17579C1.28731 9.17579 1.6613 9.54991 1.6613 10.0006C1.6613 10.9571 2.38757 11.7393 3.27865 11.7393H6.04034C6.75542 11.7393 7.34962 11.1002 7.34962 10.3307C7.34962 9.37277 7.00852 9.18573 6.44734 8.98875L2.01345 7.42562C1.15524 7.12944 0 6.49035 0 4.52196C0 2.82709 1.33125 1.4624 2.95966 1.4624H5.72121C7.52563 1.4624 8.98895 2.98154 8.98895 4.85073C8.98895 5.30279 8.61483 5.67692 8.16376 5.67692C7.71269 5.67692 7.33857 5.30279 7.33857 4.85073C7.33857 3.89417 6.61244 3.11332 5.72121 3.11332H2.95966C2.24444 3.11332 1.65038 3.75105 1.65038 4.52196C1.65038 5.47852 1.99148 5.66557 2.55252 5.86397L6.98655 7.42562C7.84462 7.72322 9 8.36095 9 10.3307C8.98895 12.0143 7.66862 13.3889 6.04034 13.3889Z" fill="white"/>
    <path d="M4.49902 14.8529C4.04795 14.8529 3.67383 14.4788 3.67383 14.0267V0.824766C3.67383 0.372704 4.04795 0 4.49902 0C4.95009 0 5.3242 0.372704 5.3242 0.824766V14.0267C5.3242 14.4788 4.95009 14.8529 4.49902 14.8529Z" fill="white"/>
  </svg>
);

const colorMap = {
  red:    'bg-[#94000042] border-[#940000] text-[#DF0A0A]',
  green:  'bg-[#00943942] border-[#009439] text-[#009439]',
  yellow: 'bg-[#94680042] border-[#946800] text-[#DFA50A]',
};

const SERVERS_PAGE_SIZE = 10;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalServers: 0, activeServers: 0, totalSpent: 0 });
  const [servers, setServers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverPage, setServerPage] = useState(1);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const serverPages = Math.max(1, Math.ceil(servers.length / SERVERS_PAGE_SIZE));
  useEffect(() => {
    setServerPage((p) => (p > serverPages ? 1 : p));
  }, [servers.length, serverPages]);

  const pagedServers = useMemo(() => {
    const start = (serverPage - 1) * SERVERS_PAGE_SIZE;
    return servers.slice(start, start + SERVERS_PAGE_SIZE);
  }, [servers, serverPage]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const [serversRes, txRes] = await Promise.all([
        axios.get(`${API_URL}/servers/my-servers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/payments/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      const serverData = serversRes.data.servers || [];
      const txData = txRes.data.transactions || [];

      const active = serverData.filter(s => s.status === 'active').length;
      const totalSpent = txData
        .filter(t => t?.status === 'completed' && typeof t?.amount === 'number' && t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

      setServers(serverData);
      setTransactions(txData);
      setStats({ totalServers: serverData.length, activeServers: active, totalSpent });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const notifications = useMemo(() => {
    const items = [];

    const DAY = 24 * 60 * 60 * 1000;
    // Expiry warnings: show all days within next 7 days
    const startOfDay = (d) => {
      const dt = new Date(d);
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    };
    const diffCalendarDays = (a, b) => {
      const a0 = startOfDay(a).getTime();
      const b0 = startOfDay(b).getTime();
      return Math.round((a0 - b0) / DAY);
    };
    const formatShortDate = (value) => {
      const d = new Date(value);
      return Number.isFinite(d.getTime())
        ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';
    };

    const now = new Date();
    const maxDays = 7;

    (servers || []).forEach((s) => {
      if (!s?.expiresAt) return;
      const exp = new Date(s.expiresAt);
      if (!Number.isFinite(exp.getTime())) return;

      const daysLeft = diffCalendarDays(exp, now);
      if (daysLeft < 0 || daysLeft > maxDays) return;

      items.push({
        text:
          daysLeft === 0
            ? `Your server ${s.name} expires today (Due: ${formatShortDate(exp)}).`
            : `Your server ${s.name} will expire in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (Due: ${formatShortDate(exp)}).`,
        label:
          daysLeft === 0
            ? 'Expires Today'
            : `${daysLeft} Day${daysLeft === 1 ? '' : 's'} Left`,
        color: daysLeft <= 1 ? 'red' : daysLeft <= 3 ? 'yellow' : 'green',
        _priority: 0,
        _sort: daysLeft,
      });
    });

    // Recent renewals/purchases (last 7 days)
    const nowMs = Date.now();
    (transactions || []).forEach((t) => {
      const created = new Date(t?.createdAt || 0).getTime();
      if (!Number.isFinite(created)) return;
      if (nowMs - created > 7 * DAY) return;
      if (String(t?.status || '').toLowerCase() !== 'completed') return;

      const serverName =
        typeof t?.serverId === 'object' && t?.serverId !== null
          ? (t.serverId.name || t.serverId.plan || 'Server')
          : 'Server';

      if (t?.type === 'server_renewal') {
        const desc = String(t?.description || '');
        const m = desc.match(/(\d+)\s*day/i);
        const days = m ? Number(m[1]) : null;
        items.push({
          text: days
            ? `${serverName} extended by ${days} day${days === 1 ? '' : 's'}.`
            : `${serverName} extended.`,
          label: days ? `Extended +${days}d` : 'Extended',
          color: 'green',
          _priority: 1,
          _sort: created ? -created : 0,
        });
      }
    });

    // Order: expiry first (most urgent), then newest extensions
    items.sort((a, b) => {
      if (a._priority !== b._priority) return a._priority - b._priority;
      return a._sort - b._sort;
    });

    return items.slice(0, 8).map(({ _priority, _sort, ...rest }) => rest);
  }, [servers, transactions]);

  const dashboardTransactions = useMemo(() => {
    return (transactions || [])
      .slice(0, 6)
      .map((t) => {
        const amt = typeof t?.amount === 'number' ? t.amount : 0;
        const status = String(t?.status || '').toLowerCase();
        const color =
          status === 'pending' ? 'yellow' :
          status === 'failed' || status === 'cancelled' || status === 'expired' ? 'red' :
          (amt >= 0 ? 'green' : 'red');
        const label =
          t?.type === 'deposit' ? 'Deposit' :
          t?.type === 'server_purchase' ? 'Server purchase' :
          t?.type === 'server_renewal' ? 'Server renewal' :
          t?.type === 'refund' ? 'Refund' :
          t?.type === 'admin_adjustment' ? 'Admin' :
          (t?.type || 'Transaction');

        const amountStr = `€${Math.abs(amt).toFixed(2)}`;
        const useClockPill =
          status === 'pending' ||
          status === 'failed' ||
          status === 'expired' ||
          status === 'cancelled';

        return { type: label, amount: amountStr, color, status, useClockPill };
      });
  }, [transactions]);

  const formatDate = (value) => {
    const d = new Date(value);
    return Number.isFinite(d.getTime())
      ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';
  };

  const toNum = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
      const cleaned = v.replace(/,/g, '');
      const match = cleaned.match(/-?\d+(\.\d+)?/);
      if (!match) return null;
      const n = Number(match[0]);
      return Number.isFinite(n) ? n : null;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const getCpuPercent = (server) => {
    const stats = server?.acerdpData?.stats || {};
    const candidates = [
      stats.cpu,
      stats.cpu_usage,
      stats.cpu_percent,
      stats.cpu_percentage,
      stats.cpu_usage_percentage,
      server?.acerdpData?.cpu,
      server?.acerdpData?.cpu_usage,
      server?.acerdpData?.cpu_percent,
      server?.cpu,
    ];

    for (const c of candidates) {
      const n = toNum(c);
      if (n === null) continue;
      const pct = n > 0 && n <= 1 ? n * 100 : n; // handle 0..1
      if (Number.isFinite(pct) && pct >= 0 && pct <= 100) return pct;
    }

    return null;
  };

  const displayName =
    user?.username?.trim() ||
    (user?.email?.includes('@') ? user.email.split('@')[0] : '') ||
    'User';

  return (
    <Layout>
      {loading ? (
        <PageLoader />
      ) : (
      <div className="flex min-h-0 flex-1 flex-col gap-6">

        {/* ── Header + Stats Cards ── */}
        <div className="flex shrink-0 flex-col gap-4 wider:flex-row lg:items-center lg:justify-between animate-enterUp">
          <div className="flex items-center gap-3 animate-enterLeft" style={{ animationDelay: '60ms' }}>
            <img
              src={logo}
              alt="logo"
              className="transition-all duration-300 ease-out hover:drop-shadow-[0_0_18px_rgba(79,107,255,0.75)]"
            />
            <div>
              <h1 className="text-3xl font-semibold text-white">Hi, {displayName}</h1>
              <p className="text-[#BABABA]">Welcome back!</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full lg:max-w-3xl animate-enterUp" style={{ animationDelay: '120ms' }}>
            {/* Servers Card */}
            <div
              className="rdp-card group relative flex h-full min-h-0 flex-col justify-center bg-no-repeat bg-top-right bg-cover border border-[#B8B8B847] p-4 sm:p-5 wide:p-5 animate-enterZoom transition-all duration-300 ease-out hover:border-blue-300/50 hover:shadow-[0_24px_60px_rgba(60,60,255,0.18)]"
              style={{ backgroundImage: `url(${priceBG})`, animationDelay: '180ms' }}>
              <div className="flex w-full min-w-0 gap-3 sm:gap-4 wide:gap-4">
                <div className="relative flex h-[64px] w-[64px] shrink-0 items-center justify-center overflow-hidden self-center rounded-full bg-[#0B0A24] bg-gradient-to-t from-[#3C3CFFC2_10%] to-[#3C3CFF00_40%] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.06] group-hover:shadow-[inset_0_0_0_1px_rgba(79,107,255,0.35),0_16px_50px_rgba(60,60,255,0.28)] motion-reduce:transition-none motion-reduce:transform-none sm:h-[72px] sm:w-[72px] lg:h-20 lg:w-20 wide:h-[90px] wide:w-[90px]">
                  <div className="pointer-events-none absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 motion-reduce:transition-none"
                    style={{ background: 'radial-gradient(circle at 30% 30%, rgba(99,102,241,0.55), rgba(60,60,255,0) 55%)' }}
                  />
                  <img src={serversImg} alt="servers" className="relative h-auto max-w-full w-7 transition-transform duration-300 ease-out group-hover:scale-110 motion-reduce:transition-none motion-reduce:transform-none sm:w-8 wide:w-10" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 sm:gap-1">
                  <p className="text-lg font-medium text-white sm:text-xl wide:text-2xl">Servers</p>
                  <div className="flex w-full min-w-0 items-baseline justify-between gap-2 sm:gap-3">
                    <p className="min-w-0 text-4xl font-semibold tabular-nums text-white sm:text-5xl wide:text-5xl">
                      {stats.totalServers}
                      <span className="ps-1.5 text-sm font-medium text-[#3C3CFF] sm:ps-2 sm:text-base wide:ps-2">Servers</span>
                    </p>
                    <Link
                      to="/services"
                      className="shrink-0 whitespace-nowrap text-xs text-[#9C9C9C] transition-colors hover:text-white sm:text-sm"
                    >
                      Show All
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions Card */}
            <div
              className="rdp-card group relative flex h-full min-h-0 flex-col justify-center bg-no-repeat bg-top-right bg-cover border border-[#B8B8B847] p-4 sm:p-5 wide:p-5 animate-enterZoom transition-all duration-300 ease-out hover:border-blue-300/50 hover:shadow-[0_24px_60px_rgba(60,60,255,0.18)]"
              style={{ backgroundImage: `url(${priceBG})`, animationDelay: '240ms' }}>
              <div className="flex w-full min-w-0 gap-3 sm:gap-4 wide:gap-4">
                <div className="relative flex h-[64px] w-[64px] shrink-0 items-center justify-center overflow-hidden self-center rounded-full bg-[#0B0A24] bg-gradient-to-t from-[#3C3CFFC2] to-[#3C3CFF00_30%] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.06] group-hover:shadow-[inset_0_0_0_1px_rgba(79,107,255,0.35),0_16px_50px_rgba(60,60,255,0.28)] motion-reduce:transition-none motion-reduce:transform-none sm:h-[72px] sm:w-[72px] lg:h-20 lg:w-20 wide:h-[90px] wide:w-[90px]">
                  <div className="pointer-events-none absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 motion-reduce:transition-none"
                    style={{ background: 'radial-gradient(circle at 30% 30%, rgba(99,102,241,0.55), rgba(60,60,255,0) 55%)' }}
                  />
                  <svg className="relative h-7 w-7 transition-transform duration-300 ease-out group-hover:scale-110 group-hover:rotate-[-3deg] motion-reduce:transition-none motion-reduce:transform-none sm:h-8 sm:w-8 wide:h-[39px] wide:w-[39px]" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M31.8728 20.3268H25.6739C21.0246 20.3268 18.6719 17.6958 18.6719 12.4867V7.12621C18.6719 5.2215 18.896 2.49665 20.8753 1.00318C22.5369 -0.230558 24.9269 -0.322003 28.4559 0.704908C33.0864 2.04927 36.9516 5.91402 38.296 10.5435C39.3229 14.0548 39.2296 16.4622 37.997 18.1048C36.5033 20.1033 33.7773 20.3268 31.8728 20.3268ZM24.3295 2.79485C23.564 2.79485 22.9664 2.94389 22.5744 3.24211C21.8462 3.78562 21.4914 5.05556 21.4914 7.12621V12.5036C21.4914 16.6858 23.0972 17.5467 25.6924 17.5467H31.8914C33.9454 17.5467 35.215 17.1908 35.7751 16.4621C36.4288 15.6035 36.3727 13.8865 35.6445 11.3661C34.5616 7.68657 31.3873 4.49509 27.7089 3.4297C26.3273 2.99921 25.2071 2.79485 24.3295 2.79485Z" fill="white"/>
                    <path d="M18.3363 38.9984C17.3467 38.9984 16.3383 38.9239 15.3302 38.7555C7.69351 37.5242 1.47576 31.3242 0.243468 23.6885C-1.34379 13.8667 4.98602 4.60529 14.6767 2.62602C15.4422 2.47692 16.1705 2.96269 16.3383 3.71062C16.4879 4.4754 16.0023 5.20419 15.2554 5.37254C7.0586 7.05119 1.68114 14.8937 3.04427 23.2413C4.0897 29.7009 9.33656 34.9485 15.797 35.9923C24.1806 37.3366 32.0039 31.9423 33.6657 23.7077C33.815 22.9406 34.562 22.4571 35.309 22.6063C36.0745 22.7554 36.5598 23.501 36.4105 24.2489C34.6368 32.9693 27.0187 38.9984 18.3363 38.9984Z" fill="white"/>
                  </svg>
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 sm:gap-1">
                  <p className="text-lg font-medium text-white sm:text-xl wide:text-2xl">Transactions</p>
                  <div className="flex w-full min-w-0 items-baseline justify-between gap-2 sm:gap-3">
                    <p className="min-w-0 text-4xl font-semibold tabular-nums text-white sm:text-5xl wide:text-5xl">
                      {transactions.length}
                      <span className="ps-1.5 text-sm font-medium text-[#3C3CFF] sm:ps-2 sm:text-base wide:ps-2">Transactions</span>
                    </p>
                    <Link
                      to="/deposits"
                      className="shrink-0 whitespace-nowrap text-xs text-[#9C9C9C] transition-colors hover:text-white sm:text-sm"
                    >
                      Show All
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Active Services: stretches to fill viewport; table body scrolls with pagination ── */}
        <div className="rdp-card flex min-h-0 flex-1 flex-col overflow-hidden border border-[#B8B8B847] bg-[#212A5533] animate-enterUp" style={{ animationDelay: '320ms' }}>
          <div className="flex shrink-0 flex-nowrap items-center justify-between gap-2 px-3 sm:px-5 py-3 sm:py-4">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              <img src={serversImg} alt="servers" className="w-4 shrink-0 sm:w-5" />
              <h2 className="truncate text-sm sm:text-lg text-white">Active Services</h2>
            </div>
            <Link to="/select-plan"
              className="inline-flex shrink-0 items-center gap-1 sm:gap-2 whitespace-nowrap rounded-xl border border-blue-300/40 bg-[#FFFFFF0F] px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-blue-100 cursor-pointer
              shadow-[inset_0_1px_1px_rgba(255,255,255,0.23),0_2px_4px_rgba(0,0,0,0.25),inset_0_-16px_28px_rgba(255,255,255,0.15)]
              transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-blue-300/60 hover:bg-[#FFFFFF14] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.23),0_18px_45px_rgba(60,60,255,0.22),inset_0_-16px_28px_rgba(255,255,255,0.15)]
              active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F6BFF]/60">
              <span>Order New Server</span>
            </Link>
          </div>

          {servers.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-10 text-center text-blue-100/70">
              <p className="mb-1 font-semibold text-white">No servers yet</p>
              <p>Your active services will appear here.</p>
            </div>
          ) : (
            <>
            <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto pt-3 font-geist sm:pt-4">
              <div className="inline-block min-w-full px-2 sm:px-4">
                <table className="min-w-full border-separate border-spacing-y-3 font-geist">
                  <thead className="text-[#BABABA] sticky top-0 z-10 backdrop-blur-md" style={{ background: 'rgba(33,42,85,0.45)' }}>
                    <tr>
                      <th className="px-2 pb-2 pt-3 text-left text-xs font-medium sm:px-4 sm:pb-3 sm:pt-3.5 sm:text-sm">No</th>
                      <th className="px-2 pb-2 pt-3 text-left text-xs font-medium sm:px-4 sm:pb-3 sm:pt-3.5 sm:text-sm">Service Name</th>
                      <th className="px-2 pb-2 pt-3 text-left text-xs font-medium sm:px-4 sm:pb-3 sm:pt-3.5 sm:text-sm">Location</th>
                      <th className="px-2 pb-2 pt-3 text-left text-xs font-medium sm:px-4 sm:pb-3 sm:pt-3.5 sm:text-sm">IP</th>
                      <th className="px-2 pb-2 pt-3 text-left text-xs font-medium sm:px-4 sm:pb-3 sm:pt-3.5 sm:text-sm">Due Date</th>
                      <th className="px-2 pb-2 pt-3 text-left text-xs font-medium sm:px-4 sm:pb-3 sm:pt-3.5 sm:text-sm">CPU</th>
                      <th className="px-2 pb-2 pt-3 text-center text-xs font-medium sm:px-4 sm:pb-3 sm:pt-3.5 sm:text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedServers.map((server, idx) => {
                      const rowNo = (serverPage - 1) * SERVERS_PAGE_SIZE + idx + 1;
                      const cpu = getCpuPercent(server);
                      const filled = cpu === null ? 0 : Math.round((cpu / 100) * 14);
                      const loc = String(server?.location || '').toLowerCase();
                      const isNl = loc === 'nl' || loc.includes('nether');

                      const serverId = server?._id || server?.id;
                      const openServer = () => {
                        if (serverId) navigate(`/server/${serverId}`);
                      };

                      return (
                        <tr
                          key={serverId ?? idx}
                          className="group cursor-pointer transition-colors"
                          onClick={openServer}
                          onKeyDown={(e) => {
                            if (!serverId) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openServer();
                            }
                          }}
                          role="link"
                          tabIndex={serverId ? 0 : -1}
                          aria-label={server?.name ? `Open server ${server.name}` : 'Open server details'}
                        >
                          <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] group-hover:bg-[#FFFFFF1c] border-y border-[#EBEBEB1C] rounded-l-xl text-white text-xs sm:text-sm transition-colors">
                            {String(rowNo).padStart(2, "0")}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] group-hover:bg-[#FFFFFF1c] border-y border-[#EBEBEB1C] text-white text-xs sm:text-sm transition-colors group-hover:text-[#93b4ff]">
                            {server?.name || '—'}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] group-hover:bg-[#FFFFFF1c] border-y border-[#EBEBEB1C] text-white text-xs sm:text-sm transition-colors">
                            <div className="flex items-center gap-1 sm:gap-2">
                              {isNl ? (
                                <img src={nl} alt="NL" className="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover" />
                              ) : (
                                <img src={usa} alt="US" className="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover" />
                              )}
                              <span className="hidden sm:inline">{isNl ? 'Netherlands' : 'United States'}</span>
                              <span className="sm:hidden">{isNl ? 'NL' : 'US'}</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] group-hover:bg-[#FFFFFF1c] border-y border-[#EBEBEB1C] text-white text-xs sm:text-sm transition-colors">
                            {server?.ip || '—'}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] group-hover:bg-[#FFFFFF1c] border-y border-[#EBEBEB1C] text-white text-xs sm:text-sm whitespace-nowrap transition-colors">
                            {formatDate(server?.expiresAt)}
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] group-hover:bg-[#FFFFFF1c] border-y border-[#EBEBEB1C] transition-colors">
                            <div className="flex items-center gap-1 sm:gap-4">
                              <div className="hidden sm:flex items-end gap-[3px]">
                                {Array.from({ length: 14 }).map((_, i) => (
                                  <div key={i} className={`w-[3px] h-6 sm:h-8 rounded-full ${i < filled ? "bg-[#4F6BFF]" : "bg-[#5E6170]"}`} />
                                ))}
                              </div>
                              <span className="text-white text-xs sm:text-sm">{cpu === null ? '—' : `${cpu}%`}</span>
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-2 sm:py-3 bg-[#FFFFFF14] group-hover:bg-[#FFFFFF1c] border-y border-[#EBEBEB1C] rounded-r-xl transition-colors">
                            <div className="flex items-center justify-center">
                              <span className={`rounded-full px-2 sm:px-6 py-1 text-xs sm:text-sm border ${server?.status === 'active' ? 'border-[#009439] text-[#009439] bg-[#00943942]' : 'border-[#940000] text-[#DF0A0A] bg-[#94000042]'}`}>
                                {server?.status === 'active' ? 'Active' : (server?.status || '—')}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <TablePagination
              page={serverPage}
              pageSize={SERVERS_PAGE_SIZE}
              total={servers.length}
              onPageChange={setServerPage}
              className="shrink-0 rounded-b-2xl bg-[#0d1229]/40"
            />
            </>
          )}
        </div>
        {/* ── Bottom Two Cards: same h-[28vh] whether empty or not ── */}
        <div className="grid shrink-0 grid-cols-1 gap-4 lg:grid-cols-2">

          {/* Notifications */}
          <div
            className="rdp-card flex h-[28vh] flex-col overflow-hidden border border-[#B8B8B847] bg-[#212A5533] animate-enterUp"
            style={{ animationDelay: '380ms' }}
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2 text-blue-50/85 sm:p-4">
              {notifications.length === 0 ? (
                <div className="flex flex-1 items-center justify-center px-6 text-center text-blue-100/70">
                  <p>No notifications yet.</p>
                </div>
              ) : (
                notifications.map((n, i) => (
                  <div key={i} className="bg-[#FFFFFF14] rounded-xl border border-[#EBEBEB1C] flex items-center justify-between gap-2 px-2 sm:px-3 py-1.5 sm:py-2">
                    <span className="text-xs sm:text-sm">{n.text}</span>
                    <div className={`shrink-0 border px-2 sm:px-4 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm ${colorMap[n.color]}`}>{n.label}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Transactions */}
          <div
            className="rdp-card flex h-[28vh] flex-col overflow-hidden border border-[#B8B8B847] bg-[#212A5533] animate-enterUp"
            style={{ animationDelay: '440ms' }}
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto space-y-2 p-2 text-blue-50/85 sm:p-4">
              {dashboardTransactions.length === 0 ? (
                <div className="flex flex-1 items-center justify-center px-6 text-center text-blue-100/70">
                  <p>No transactions yet.</p>
                </div>
              ) : (
                dashboardTransactions.map((t, i) => (
                  <div key={i} className="bg-[#FFFFFF14] rounded-xl border border-[#EBEBEB1C] flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="bg-gradient-to-t from-[#3C3CFFC2] to-[#3C3CFF00] border border-[#FFFFFF2B] rounded-full w-[22px] h-[22px] sm:w-[29px] sm:h-[29px] flex items-center justify-center shrink-0">
                        <DollarIcon />
                      </div>
                      <p className="text-xs sm:text-sm">{t.type}</p>
                    </div>
                    <div className={`flex items-center justify-center border px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm ${colorMap[t.color]}`}>
                      {t.useClockPill ? (
                        <span className="inline-flex items-center gap-1">
                          {t.status === 'expired' ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path
                                d="M12 8v5l3 2"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M21 12a9 9 0 1 1-18 0a9 9 0 0 1 18 0Z"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          )}
                          {t.amount}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <span className={t.color === 'red' ? 'text-[#DF0A0A]' : 'text-[#009439]'}>{t.color === 'red' ? '-' : '+'}</span>
                          {t.amount}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      </div>
      )}
    </Layout>
  );
};

export default Dashboard;